import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { memberKey } from "../../lib/team";

const ctl = "flex h-7 w-7 items-center justify-center rounded-full border border-border bg-bone text-olive transition hover:border-ink hover:text-ink disabled:opacity-30";

const Row = ({ person, index, count, onMove, onDrop, children }) => {
  const controls = useDragControls();
  const key = memberKey(person.name);
  return (
    <Reorder.Item as="div" value={person} dragListener={false} dragControls={controls} onDragEnd={onDrop}
      className="relative" data-testid={`team-reorder-item-${key}`}>
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1">
        <button type="button" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label={`Move ${person.name} up`} data-testid={`team-move-up-${key}`} className={ctl}>
          <ChevronUp className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
        <button type="button" disabled={index === count - 1} onClick={() => onMove(index, index + 1)} aria-label={`Move ${person.name} down`} data-testid={`team-move-down-${key}`} className={ctl}>
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
        <button type="button" onPointerDown={(e) => controls.start(e)} aria-label={`Drag to reorder ${person.name}`} data-testid={`team-drag-${key}`}
          className={`${ctl} cursor-grab touch-none active:cursor-grabbing`}>
          <GripVertical className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>
      {children}
    </Reorder.Item>
  );
};

/**
 * Drag-to-reorder list of one group's cards on /admin/team. Order saves on drop
 * (or on an arrow click) to PUT /api/admin/team/order and applies to /team.
 */
export const TeamReorderList = ({ group, people, client, onOrderSaved, renderCard }) => {
  const [items, setItems] = useState(people);
  const latest = useRef(people);
  useEffect(() => { setItems(people); latest.current = people; }, [people]);

  const save = async (list) => {
    const keys = list.map((p) => memberKey(p.name));
    try {
      const res = await client.put("/admin/team/order", { group, keys });
      onOrderSaved(res.data);
      toast.success("Order saved.", { description: "Live on /team now." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the order.");
    }
  };

  const onReorder = (next) => { latest.current = next; setItems(next); };
  const onMove = (from, to) => {
    const next = [...latest.current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
    save(next);
  };

  return (
    <Reorder.Group as="div" axis="y" values={items} onReorder={onReorder} className="mt-4 space-y-4" data-testid={`team-reorder-${group}`}>
      {items.map((p, i) => (
        <Row key={memberKey(p.name)} person={p} index={i} count={items.length} onMove={onMove} onDrop={() => save(latest.current)}>
          {renderCard(p)}
        </Row>
      ))}
    </Reorder.Group>
  );
};
